import { useEffect, useRef } from 'react';
import qrcode from 'qrcode-generator';

/**
 * A QR code drawn straight to a canvas.
 *
 * Used for the chain onboarding link: "set somewhere" includes printing the
 * URL in a back office, and nobody types a 32-character token by hand.
 *
 * Drawn at devicePixelRatio so it stays sharp when printed, and with error
 * correction 'M' — a printed page picks up creases and coffee rings, and a
 * link that scans on the second try is worth the extra modules.
 */
export function QrCode({
  value,
  size = 132,
  className,
}: {
  value: string;
  size?: number;
  className?: string;
}) {
  const ref = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas || !value) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let modules: number;
    let isDark: (r: number, c: number) => boolean;
    try {
      const qr = qrcode(0, 'M');
      qr.addData(value);
      qr.make();
      modules = qr.getModuleCount();
      isDark = (r, c) => qr.isDark(r, c);
    } catch {
      // A QR failure must never take the panel down — the copy button next to
      // it is the real path anyway.
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      return;
    }

    const dpr = window.devicePixelRatio || 1;
    const quiet = 2; // modules of white margin — scanners need it
    const total = modules + quiet * 2;
    const cell = Math.max(1, Math.floor((size * dpr) / total));
    const px = cell * total;

    canvas.width = px;
    canvas.height = px;
    canvas.style.width = `${size}px`;
    canvas.style.height = `${size}px`;

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, px, px);
    ctx.fillStyle = '#1F4E79'; // Brix navy
    for (let r = 0; r < modules; r += 1) {
      for (let c = 0; c < modules; c += 1) {
        if (isDark(r, c)) {
          ctx.fillRect((c + quiet) * cell, (r + quiet) * cell, cell, cell);
        }
      }
    }
  }, [value, size]);

  return <canvas ref={ref} className={className} aria-label="QR code for this link" role="img" />;
}
